import type {
  CreativeDecisionMoneySummaryBucket,
  MoneyAmountBinding,
  ValueBasisBinding,
} from '../../creativeDecisions';

export function formatMoneyBinding(
  binding: MoneyAmountBinding | undefined,
  preferReporting = false,
): string {
  if (!binding) return 'n/a';
  const useReporting = preferReporting
    && binding.reportingAmountMinor !== undefined
    && Boolean(binding.reportingCurrency)
    && binding.reportingCurrencyExponent !== undefined;
  return formatMinorMoney(
    useReporting ? binding.reportingAmountMinor as number : binding.amountMinor,
    useReporting ? binding.reportingCurrency as string : binding.originalCurrency,
    useReporting ? binding.reportingCurrencyExponent as number : binding.currencyExponent,
  );
}

export function formatSummaryMoney(
  bucket: CreativeDecisionMoneySummaryBucket,
  field: keyof Pick<
    CreativeDecisionMoneySummaryBucket,
    | 'selectedWindowSpendMinor'
    | 'currentDailySpendMinor'
    | 'spendNeedingAttentionMinor'
    | 'unchangedPaceSevenDayExposureMinor'
    | 'unreconciledSpendMinor'
    | 'pendingSettlementSpendMinor'
    | 'trustBlockedSpendMinor'
    | 'currentPartialDaySpendMinor'
  >,
): string {
  return formatMinorMoney(bucket[field], bucket.originalCurrency, bucket.currencyExponent);
}

export function formatMinorMoney(
  amountMinor: number,
  currency: string,
  exponent: number,
  options?: {
    /**
     * I5a review MF-1: the default 'symbol' display renders «US$» outside a
     * US locale — three occurrences push the watch-only chip label past its
     * 260px column and truncate the load-bearing «watch-only» disclaimer.
     * Compact chip labels pass narrow=true for «$»; summary tiles keep the
     * unambiguous default.
     */
    narrow?: boolean;
  },
): string {
  const normalizedCurrency = currency.toUpperCase();
  const amount = amountMinor / 10 ** exponent;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalizedCurrency,
      ...(options?.narrow ? { currencyDisplay: 'narrowSymbol' as const } : {}),
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(amount);
  } catch {
    return `${normalizedCurrency} ${amount.toFixed(exponent)}`;
  }
}

export function formatPercent(value: number | undefined, digits = 1): string {
  return value === undefined || !Number.isFinite(value) ? 'n/a' : `${(value * 100).toFixed(digits)}%`;
}

export function formatRoas(value: number | undefined, digits = 2): string {
  return value === undefined || !Number.isFinite(value) ? 'n/a' : `${value.toFixed(digits)}×`;
}

export function formatSampleCount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value) || value < 0) return 'n/a';
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
}

/**
 * AC-H-8: fail-closed policy states get human words naming the fix; every
 * other code keeps the legacy de-underscored fallback. Extend this map instead
 * of sprinkling `replaceAll('_', ' ')` at render sites.
 *
 * I5d (unlock-prod-money-values): the money-withholding vocabulary — every
 * shipped money reason code maps to an operator phrase (UNLOCK-AC-047);
 * unknown codes keep degrading to the readable de-underscore fallback
 * (UNLOCK-AC-048). Raw snake_case never reaches primary copy (UNLOCK-AC-049)
 * — raw codes live in hover titles / technical disclosures only.
 */
export function humanizeCreativeReasonCode(code: string | undefined): string | undefined {
  if (!code) return undefined;
  switch (code) {
    case 'money_reporting_policy_version_unverified':
    case 'money_reporting_policy_missing_or_conflicting':
      return 'money reporting policy not configured';
    case 'money_reporting_policy_version_mismatch':
      return 'money reporting policy version mismatch';
    case 'first_party_reporting_fx_incomplete_or_conflicting':
      return 'reporting FX incomplete — check money reporting policy';
    case 'creative_decision_policy_not_registered':
      return 'decision policy not registered';
    case 'incomplete_cycle_value_coverage':
      return 'some billing cycles not yet valued';
    case 'model_inputs_unavailable':
      return 'not enough renewal history to project yet';
    case 'precision_unavailable':
      return 'amounts pending exact-value confirmation';
    case 'currency_or_versioned_fx_incompatible':
      return 'currency conversion evidence missing — check the money reporting policy';
    case 'basis_unavailable':
      return 'this value basis is not computed for this window';
    case 'no_billing_opportunity_yet':
      return 'no renewals have come due yet';
    case 'settlement_evidence_pending':
      return 'bank settlement confirmation pending';
    // W-A vocabulary split (DISC-AC-016): the *_cycle_unreconciled family is
    // NOT one phrase — each basis blocks for a different mechanism.
    case 'gross_cycle_unreconciled':
      return 'newest payments await their provider controls';
    case 'net_collected_cycle_unreconciled':
      return 'net components await refund & dispute matching';
    case 'settlement_cycle_unreconciled':
      return 'settlement evidence pipeline is not producing daily movements';
    case 'contribution_cycle_unreconciled':
      return 'cost categories (tax, COGS, credits, discounts) have no control source yet';
    case 'gross_unreconciled':
      return 'gross money not yet verified against provider controls';
    case 'net_components_unreconciled':
      return 'refund/dispute components not yet verified';
    case 'settlement_components_unreconciled':
      return 'settlement components not yet verified';
    case 'contribution_components_unreconciled':
      return 'cost components not yet verified';
    case 'missing_customer_value_fact':
      return 'no value record exists for this subscription yet';
    case 'no_value_evidence':
      return 'value record carries no payment timestamps';
    case 'all_cycles_pending_settlement':
      return 'every payment sits beyond the verified boundary';
    case 'provider_account_ambiguous':
      return 'payments span more than one provider account';
    case 'provider_account_identity_missing':
      // DISC-AC-147 (F-W-A-PROD-1): identity-less value rows refuse instead
      // of silently passing the single-account gate.
      return 'payment records carry no provider account identity';
    case 'external_or_unknown_billing_origin':
      return 'billing origin is unknown — excluded until classified';
    case 'reconciliation_as_of_missing':
      return 'no reconciliation timestamp recorded yet';
    case 'currency_unavailable':
      return 'currency evidence missing on the value record';
    case 'basis_policy_version_missing':
      return 'no money-reporting policy version bound';
    case 'model_not_projectable':
      return 'cohort shape does not fit the projection model';
    case 'provider_spend_control_stale':
      return 'spend controls have aged past the freshness window';
    case 'cause_unresolved':
      return 'withheld for a cause the diagnosis could not resolve yet';
    default:
      // Unknown codes degrade to the readable de-underscore fallback — they
      // stay VISIBLE so they can be mapped (DISC-AC-033).
      return code.replaceAll('_', ' ');
  }
}

/**
 * I5d: self-heal expectation per money reason code (owner Q6: fixed
 * vocabulary defaults; server-supplied horizons override where the diagnosis
 * knows them). States the MECHANISM or a calibrated horizon; operator-action
 * states name the need instead — a horizon is never fabricated for them.
 * Unknown codes return undefined: no expectation is invented.
 */
export function creativeReasonSelfHealNote(code: string | undefined): string | undefined {
  switch (code) {
    // W-A split (DISC-AC-016/027): three distinct mechanisms. LIVE EDGE —
    // self-heals as provider settlement days close.
    case 'gross_cycle_unreconciled':
    case 'gross_unreconciled':
    case 'all_cycles_pending_settlement':
      return 'self-heals as provider days close (~2 days)';
    // WAREHOUSE PASS LAG — self-heals on the next materialization pass.
    case 'incomplete_cycle_value_coverage':
    case 'precision_unavailable':
      return 'after the next warehouse pass';
    // COHORT TIME — grows on its own.
    case 'model_inputs_unavailable':
    case 'no_billing_opportunity_yet':
      return 'grows with the cohort';
    case 'settlement_evidence_pending':
      return 'finalizes in ~14 days';
    // POLICY / PIPELINE BLOCKS — NO horizon is ever promised (DISC-AC-027);
    // the note names the lever instead.
    case 'net_collected_cycle_unreconciled':
    case 'net_components_unreconciled':
      return 'opens with the refunds & disputes truth wave';
    case 'settlement_cycle_unreconciled':
    case 'settlement_components_unreconciled':
      return 'needs the settlement pipeline repair';
    case 'contribution_cycle_unreconciled':
    case 'contribution_components_unreconciled':
      return 'needs a coverage-registry decision for the no-source categories';
    case 'currency_or_versioned_fx_incompatible':
    case 'basis_policy_version_missing':
      return 'needs operator action';
    case 'provider_spend_control_stale':
      return 'run a delivery re-sync to refresh';
    default:
      return undefined;
  }
}

/**
 * DISC-AC-027: a horizon is promised ONLY for the mechanisms that close on
 * their own (provider settlement days, a warehouse pass, cohort time). Policy
 * and pipeline blocks name a LEVER instead, and a surface that reads «no
 * action is needed» over «needs a coverage-registry decision» is telling the
 * operator two different things in one alert. Classification lives here with
 * the codes, not in a component (DISC-AC-030).
 */
export function creativeReasonClearsOnItsOwn(code: string | undefined): boolean {
  switch (code) {
    case 'gross_cycle_unreconciled':
    case 'gross_unreconciled':
    case 'all_cycles_pending_settlement':
    case 'incomplete_cycle_value_coverage':
    case 'precision_unavailable':
    case 'model_inputs_unavailable':
    case 'no_billing_opportunity_yet':
    case 'settlement_evidence_pending':
      return true;
    default:
      return false;
  }
}

export function valueBasisLabel(binding: ValueBasisBinding | undefined): string {
  if (!binding) return 'basis unavailable';
  if (binding.availability === 'unavailable') {
    const reason = humanizeCreativeReasonCode(binding.unavailableReasons?.[0]);
    return `${binding.basis.replaceAll('_', ' ')} · unavailable${reason ? ` · ${reason}` : ''}`;
  }
  const coverage = binding.componentCoverage.some((component) => component.state === 'partial')
    ? 'partial'
    : binding.componentCoverage.some((component) => component.state === 'unavailable')
      ? 'incomplete'
      : 'complete';
  return `${binding.basis.replaceAll('_', ' ')} · ${coverage}`;
}
